import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Download, Users, CheckCircle, RefreshCcw, Lock, Trash2 } from 'lucide-react';
import PageBanner from './PageBanner';
import { motion } from 'framer-motion';

const unitThemes = {
  cs: { name: 'Computer Society', color: '#F6A813', icon: '/asset/logos/cswh.png' },
  ias: { name: 'Industrial Applications Society', color: '#00629B', icon: '/asset/logos/iasjdid.png' },
  'ies-pes': { name: 'Power & Energy Society', color: '#008542', icon: '/asset/logos/iespeswh.png' },
  wie: { name: 'Women in Engineering', color: '#781F72', icon: '/asset/logos/wiejdid.png' },
  sight: { name: 'Humanitarian Technology', color: '#00629B', icon: '/asset/logos/sightwh.png' },
  smc: { name: 'Systems, Man & Cybernetics', color: '#00629B', icon: '/asset/logos/smcwh.png' },
  ras: { name: 'Robotics & Automation', color: '#8B0000', icon: '/asset/logos/raswh.png' },
};

export default function UnitAdminDashboard({ unit }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const theme = unitThemes[unit] || { name: unit.toUpperCase(), color: '#006699' };
  const tableName = `${unit.replace('-', '')}_registrations`;

  const handleLogin = (e) => {
    e.preventDefault();
    const expectedPassword = `${unit}isims123*`;
    if (password === expectedPassword) {
      setIsAuthenticated(true);
      fetchRegistrations();
    } else {
      toast.error('Incorrect password');
    }
  };

  const fetchRegistrations = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRegistrations(data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load registrations. Check if the table exists.');
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    if (registrations.length === 0) return;
    const headers = ['Date', 'Full Name', 'Email', 'Phone', 'Affiliation', 'Comments'];
    const csvContent = [
      headers.join(','),
      ...registrations.map(reg => [
        `"${new Date(reg.created_at).toLocaleDateString()}"`,
        `"${reg.full_name || ''}"`,
        `"${reg.email || ''}"`,
        `"${reg.phone || ''}"`,
        `"${reg.affiliation || ''}"`,
        `"${reg.comments?.replace(/\n/g, ' ') || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${unit}_registrations.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isAuthenticated) {
    return (
      <div key={unit} className="min-h-screen bg-gray-50 flex items-center justify-center p-4 relative">
        <motion.div 
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl border-t-4" 
          style={{ borderColor: theme.color }}
        >
          <div className="text-center mb-8 flex flex-col items-center">
            {theme.icon ? (
              <div className="w-24 h-24 sm:w-32 sm:h-32 bg-gray-100 rounded-full flex items-center justify-center mb-4 p-4 shadow-inner mx-auto">
                <img src={theme.icon} alt={theme.name} className="w-full h-full object-contain" style={{ filter: 'brightness(0) drop-shadow(0px 2px 2px rgba(0,0,0,0.1))' }} />
              </div>
            ) : (
              <Lock className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4" style={{ color: theme.color }} />
            )}
            <h2 className="text-2xl font-bold text-gray-900 text-center">{theme.name} Admin</h2>
            <p className="text-gray-500 text-sm mt-2 text-center">Enter password to access dashboard</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2"
                style={{ focusRingColor: theme.color }}
                placeholder="Password"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full text-white font-bold py-3 px-4 rounded transition-colors"
              style={{ backgroundColor: theme.color }}
            >
              Login
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div key={unit} className="min-h-screen bg-gray-50 pb-12">
      <PageBanner
        title={`${theme.name} Admin`}
        description="Manage your chapter's registrations"
        Icon={Users}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
          <div className="flex items-center gap-4">
            {theme.icon && (
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-lg shadow-sm border border-gray-100 p-2 flex items-center justify-center">
                <img src={theme.icon} alt={theme.name} className="w-full h-full object-contain" style={{ filter: 'brightness(0)' }} />
              </div>
            )}
            <h1 className="text-2xl font-bold text-gray-900">Registrations <span className="text-gray-500 text-lg">({registrations.length})</span></h1>
          </div>
          <div className="flex gap-4">
            <button
              onClick={fetchRegistrations}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
            >
              <RefreshCcw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-md transition-colors text-sm font-medium hover:opacity-90"
              style={{ backgroundColor: theme.color }}
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-current rounded-full animate-spin mx-auto" style={{ color: theme.color }} />
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white shadow rounded-lg overflow-hidden border border-gray-200"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Affiliation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comments</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {registrations.map((reg, index) => (
                    <motion.tr 
                      key={reg.id} 
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(reg.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{reg.full_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{reg.email}</div>
                        <div className="text-sm text-gray-500">{reg.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {reg.affiliation}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {reg.comments || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-center">
                        <button
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to delete ${reg.full_name}?`)) {
                              const { error } = await supabase.from(tableName).delete().eq('id', reg.id);
                              if (error) {
                                toast.error(`Failed to delete: ${error.message}`);
                              } else {
                                toast.success("Registration deleted");
                                setRegistrations(prev => prev.filter(r => r.id !== reg.id));
                              }
                            }
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                  {registrations.length === 0 && (
                    <motion.tr 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        No registrations found.
                      </td>
                    </motion.tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
